import { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Input } from '@/components/cms/ui/input';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { processGeocodingResult } from '@/utils/locationUtils';
import { Panel, PanelAnchor, PanelBody, PanelContent, PanelSection } from '@/components/ui/panel';
import { ListRow } from '@/components/ui/list-row';

interface Props {
  className?: string;
  onChange: (patch: { address?: string; latitude?: string; longitude?: string; city?: string }) => void;
}

function geocode(request: google.maps.GeocoderRequest): Promise<google.maps.GeocoderResult[]> {
  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(request, (results, status) => {
      if (status === 'OK' && results) resolve(results);
      else reject(new Error(`Geocoding failed: ${status}`));
    });
  });
}

export function LocationSearch({ className = '', onChange }: Props) {
  const [isReady, setIsReady] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [autocompleteResults, setAutocompleteResults] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const check = () => {
      attempts++;
      if (typeof google !== 'undefined' && google.maps?.Geocoder && google.maps.places) {
        setIsReady(true);
        setAutocompleteService(new google.maps.places.AutocompleteService());
      } else if (attempts < 50) {
        timeoutId = setTimeout(check, 100);
      }
    };
    check();
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const applyResult = (result: google.maps.GeocoderResult, fallbackAddress: string) => {
    const info = processGeocodingResult(result);
    onChange({
      latitude: String(info.coordinates.lat),
      longitude: String(info.coordinates.lng),
      address: info.address || fallbackAddress,
      city: info.city ?? '',
    });
  };

  const runAutocomplete = (query: string) => {
    if (!autocompleteService || !query.trim()) {
      setAutocompleteResults([]);
      setShowAutocomplete(false);
      return;
    }
    autocompleteService.getPlacePredictions({ input: query, types: ['geocode', 'establishment'] }, (preds, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && preds) {
        setAutocompleteResults(preds);
        setShowAutocomplete(true);
      } else {
        setAutocompleteResults([]);
        setShowAutocomplete(false);
      }
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runAutocomplete(value), 250);
  };

  const handleSelect = async (prediction: google.maps.places.AutocompletePrediction) => {
    setShowAutocomplete(false);
    setSearchQuery(prediction.structured_formatting?.main_text ?? prediction.description);
    try {
      const results = await geocode({ placeId: prediction.place_id });
      if (results[0]) applyResult(results[0], prediction.description);
    } catch (e) {
      console.error('Place lookup failed:', e);
    }
  };

  const handleSearchEnter = async () => {
    if (!searchQuery.trim()) return;
    setShowAutocomplete(false);
    setIsSearching(true);
    try {
      const results = await geocode({ address: searchQuery });
      if (results[0]) applyResult(results[0], searchQuery);
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Panel
      open={showAutocomplete && autocompleteResults.length > 0}
      onOpenChange={(next) => {
        if (!next) setShowAutocomplete(false);
      }}
    >
      <PanelAnchor asChild>
        <div className={`relative flex items-center ${className}`}>
          <MagnifyingGlassIcon className="absolute left-2.5 h-4 w-4 text-lureo-ink-subtle" />
          <Input
            type="text"
            placeholder="Search for a location…"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleSearchEnter();
              }
            }}
            disabled={!isReady || isSearching}
            className="pl-8 pr-8"
          />
          {isSearching && (
            <div className="absolute right-2.5">
              <LoadingSpinner size="sm" />
            </div>
          )}
        </div>
      </PanelAnchor>

      <PanelContent width="sm" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        <PanelBody>
          <PanelSection>
            {autocompleteResults.map((prediction) => (
              <ListRow
                key={prediction.place_id}
                label={prediction.structured_formatting?.main_text ?? prediction.description}
                description={prediction.structured_formatting?.secondary_text}
                onMouseDown={(e) => {
                  e.preventDefault();
                  void handleSelect(prediction);
                }}
              />
            ))}
          </PanelSection>
        </PanelBody>
      </PanelContent>
    </Panel>
  );
}
